import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusBadge } from '@/components/StatusBadge';
import { api } from '@/lib/api';
import { formatNumber, formatINR, formatDateTime } from '@/lib/format';
import { Landmark, AlertTriangle, Upload, Diamond, Download } from 'lucide-react';
import { toast } from 'sonner';
import { ScreenshotsPicker, BankFields, validateBank } from '@/components/BankTransferFields';
import { StockWalletLocked } from '@/components/StockWalletLocked';

const MODES = ['NEFT', 'RTGS'];
const MIN_STOCK_INR = 5000;
const EMPTY = { platform: 'trumeet', amount_inr: '', real_name: '', account_number: '', transfer_date: '', transfer_time: '', mode: 'NEFT', utr: '', bank_name: '', ifsc: '' };

export default function UserStockWallet() {
    const [config, setConfig] = useState([]);
    const [orders, setOrders] = useState([]);
    const [open, setOpen] = useState(false);
    const [bank, setBank] = useState({});
    const [form, setForm] = useState(EMPTY);
    const [files, setFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [downloading, setDownloading] = useState(null);
    const [unlocked, setUnlocked] = useState(null);

    const loadConfig = () => api.get('/recharge/stock/config').then(({ data }) => { setConfig(data.platforms || []); setUnlocked(!!data.wallet_unlocked); }).catch(() => {});
    const loadOrders = () => api.get('/recharge/stock/orders').then(({ data }) => setOrders(data.orders || [])).catch(() => {});
    useEffect(() => { loadConfig(); loadOrders(); }, []);
    useEffect(() => {
        if (!open) return;
        api.get('/recharge/stock/bank-details').then(({ data }) => setBank(data.bank || {})).catch(() => {});
    }, [open]);

    const price = (config.find(p => p.key === form.platform) || {}).price || 0;
    const amt = parseFloat(form.amount_inr) || 0;
    const diamonds = price > 0 ? Math.round(amt / price) : 0;

    const submit = async () => {
        if (amt < MIN_STOCK_INR) { toast.error(`Minimum stock purchase is ₹${MIN_STOCK_INR.toLocaleString('en-IN')}`); return; }
        if (!files.length) { toast.error('Attach at least one payment screenshot'); return; }
        const bankErr = validateBank(form); if (bankErr) { toast.error(bankErr); return; }
        for (const [k, label] of [['real_name', 'real name'], ['account_number', 'account number'], ['transfer_date', 'date'], ['utr', 'UTR number']]) {
            if (!String(form[k] || '').trim()) { toast.error(`Please enter the ${label}`); return; }
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            files.forEach((f) => fd.append('screenshots', f));
            await api.post('/recharge/stock/bank-transfer', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(`Transfer submitted! ${diamonds.toLocaleString()} 💎 pending admin verification.`);
            setOpen(false); setForm(EMPTY); setFiles([]); loadOrders();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not submit');
        } finally { setSubmitting(false); }
    };

    const downloadInvoice = async (o) => {
        setDownloading(o.id);
        try {
            const res = await api.get(`/recharge/stock/invoice/${o.id}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url; a.download = `stock-invoice-${o.invoice_number || o.id}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch { toast.error('Invoice not available yet'); }
        finally { setDownloading(null); }
    };

    return (
        <div className="space-y-4" data-testid="user-stock-wallet">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {config.map(c => (
                    <Card key={c.key} className="p-4" data-testid={`user-stock-balance-${c.key}`}>
                        <div className="text-xs text-muted-foreground">{c.name} stock</div>
                        <div className="mt-1 flex items-center gap-2 font-display text-2xl font-semibold tabular-nums">
                            <Diamond className="h-5 w-5 text-[#1F9D8A]" /> {formatNumber(c.balance ?? 0)}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">₹{Number(c.price).toFixed(2)} / 💎</div>
                    </Card>
                ))}
            </div>

            {unlocked === false && <StockWalletLocked />}
            {unlocked && <Card className="p-4 sm:p-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="font-display font-semibold">Add stock via bank transfer</div>
                        <p className="text-xs text-muted-foreground mt-0.5">Transfer funds, submit the details, and your stock is credited after admin approval.</p>
                    </div>
                    <Button onClick={() => setOpen(true)} className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="user-add-stock-button">
                        <Landmark className="h-4 w-4 mr-2" /> Add funds via bank transfer
                    </Button>
                </div>
            </Card>}

            <Card className="p-4 sm:p-5">
                <div className="font-display font-semibold mb-3">Your stock orders</div>
                <div className="overflow-x-auto" data-testid="user-stock-orders-table">
                    <Table>
                        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Platform</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Diamonds</TableHead><TableHead>Invoice #</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Invoice</TableHead></TableRow></TableHeader>
                        <TableBody>
                            {orders.map(o => (
                                <TableRow key={o.id} data-testid={`user-stock-order-${o.id}`}>
                                    <TableCell className="whitespace-nowrap">{formatDateTime(o.created_at)}</TableCell>
                                    <TableCell>{o.platform_name}</TableCell>
                                    <TableCell className="text-right">{formatINR(o.amount_inr)}</TableCell>
                                    <TableCell className="text-right tabular-nums">{formatNumber(o.diamonds)}</TableCell>
                                    <TableCell className="font-mono text-xs">{o.invoice_number || '—'}</TableCell>
                                    <TableCell><StatusBadge status={o.status} /></TableCell>
                                    <TableCell className="text-right">
                                        {['pending', 'approved'].includes(o.status) ? (
                                            <Button size="sm" variant="outline" onClick={() => downloadInvoice(o)} disabled={downloading === o.id} data-testid={`user-stock-invoice-${o.id}`}>
                                                <Download className="h-3.5 w-3.5 mr-1" /> {downloading === o.id ? '…' : 'PDF'}
                                            </Button>
                                        ) : <span className="text-xs text-muted-foreground">—</span>}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {orders.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No stock orders yet.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="user-bank-transfer-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2"><Landmark className="w-5 h-5" /> Bank transfer top-up</DialogTitle>
                        <DialogDescription>Transfer via NEFT or RTGS only (minimum ₹5,000), then submit the details below. Stock is credited after admin verifies the payment.</DialogDescription>
                    </DialogHeader>

                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex gap-2 text-[13px] text-amber-900" data-testid="user-bt-warning">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>Your <strong>real name</strong> must match the payee/bank account name. If it doesn't match, the payment will be <strong>refunded to the source account</strong>.</span>
                    </div>

                    {bank.account_number && (
                        <div className="rounded-lg border border-border bg-secondary/50 p-3 text-sm" data-testid="user-bt-destination">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Transfer funds to</div>
                            {bank.account_name && <div><span className="text-muted-foreground">Name:</span> <strong>{bank.account_name}</strong></div>}
                            {bank.account_number && <div><span className="text-muted-foreground">A/C:</span> <strong className="font-mono">{bank.account_number}</strong></div>}
                            {bank.ifsc && <div><span className="text-muted-foreground">IFSC:</span> <strong className="font-mono">{bank.ifsc}</strong></div>}
                            {bank.bank_name && <div><span className="text-muted-foreground">Bank:</span> {bank.bank_name}</div>}
                            {bank.notes && <div className="text-xs text-muted-foreground mt-1">{bank.notes}</div>}
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs">Platform</Label>
                            <Select value={form.platform} onValueChange={v => setForm(f => ({ ...f, platform: v }))}>
                                <SelectTrigger data-testid="user-bt-platform"><SelectValue /></SelectTrigger>
                                <SelectContent>{config.map(p => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Amount transferred (₹)</Label>
                            <Input type="number" min={MIN_STOCK_INR} step="1" placeholder="Min ₹5,000" value={form.amount_inr} onChange={e => setForm(f => ({ ...f, amount_inr: e.target.value }))} data-testid="user-bt-amount" />
                            {form.amount_inr !== '' && amt < MIN_STOCK_INR && <p className="text-[11px] text-rose-600" data-testid="user-bt-amount-min-error">Minimum ₹5,000</p>}
                        </div>
                    </div>

                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center justify-between text-sm" data-testid="user-bt-preview">
                        <span className="text-emerald-900">Stock (after approval)</span>
                        <span className="font-display font-semibold text-emerald-900">{amt >= MIN_STOCK_INR ? `${diamonds.toLocaleString()} 💎` : 'Enter ₹5,000 or more'}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5 col-span-2">
                            <Label className="text-xs">Your real name (as per bank)</Label>
                            <Input value={form.real_name} onChange={e => setForm(f => ({ ...f, real_name: e.target.value }))} data-testid="user-bt-real-name" />
                        </div>
                        <div className="space-y-1.5 col-span-2">
                            <Label className="text-xs">Account number (from which you paid)</Label>
                            <Input value={form.account_number} onChange={e => setForm(f => ({ ...f, account_number: e.target.value }))} data-testid="user-bt-account-number" />
                        </div>
                        <BankFields form={form} setForm={setForm} prefix="user-bt" />
                        <div className="space-y-1.5">
                            <Label className="text-xs">Transfer date</Label>
                            <Input type="date" value={form.transfer_date} onChange={e => setForm(f => ({ ...f, transfer_date: e.target.value }))} data-testid="user-bt-date" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Transfer time</Label>
                            <Input type="time" value={form.transfer_time} onChange={e => setForm(f => ({ ...f, transfer_time: e.target.value }))} data-testid="user-bt-time" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Mode</Label>
                            <Select value={form.mode} onValueChange={v => setForm(f => ({ ...f, mode: v }))}>
                                <SelectTrigger data-testid="user-bt-mode"><SelectValue /></SelectTrigger>
                                <SelectContent>{MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">UTR / reference no.</Label>
                            <Input value={form.utr} onChange={e => setForm(f => ({ ...f, utr: e.target.value }))} data-testid="user-bt-utr" />
                        </div>
                    </div>

                    <ScreenshotsPicker files={files} onChange={setFiles} prefix="user-bt" />

                    <Button onClick={submit} disabled={submitting || amt < MIN_STOCK_INR} className="w-full bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="user-bt-submit">
                        {submitting ? 'Submitting…' : 'Submit for verification'}
                    </Button>
                </DialogContent>
            </Dialog>
        </div>
    );
}
